import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";

import { AddressPopupLabels, AddressPopupPropTypes } from "./types";
import Button from "../Button";
import SimpleButton from "../Button/SimpleButton";
import { getSvgIcon } from "../../Helpers/get-assets";
import { X, Info as InfoIcon } from "react-feather";
import ForcePickupNotification from "./components/ForcePickupNotification";
import DeliveryTypeSwitcher from "./components/DeliveryTypeSwitcher";
import OrderTypeTabs from "./components/OrderTypeTabs";
import useFeatureFlags from "~/assets/js/Helpers/hooks/useFeatureFlags";
import GoogleTagUtil from "~/assets/js/Helpers/google-tag-util";
import { TABS } from ".";
import i18next from "i18next";
import TextInput from "~/assets/js/components/Forms/TextInput";

export const DELIVERY_TYPES = {
    DELIVERY: "delivery",
    PICKUP: "pickup",
};

export const initialFormikValues = {
    zip: "",
    location: "",
    selectedLocation: null,
};

const ZIP_LENGTH = 5;

const AddressPopup = ({
    labels,
    suggestions,
    setLocationQuery,
    pickupAreas,
    forcePickup = false,
    setOpen = () => null,
    setForcePickup = () => null,
    onAddressSelect = () => null,
    checkPlaceDetails = () => null,
    forceSelection = false,
    noHouseNumberError,
    forceOnGroupOrder = null,
    openTab,
    tab,
    setTab,
}) => {
    const [deliveryType, setDeliveryType] = useState(
        openTab ?? DELIVERY_TYPES.DELIVERY
    );
    const bicycleIcon = getSvgIcon(`bicycle-new.svg`);
    const shopIcon = getSvgIcon(`shop-new.svg`);
    const dangerIcon = getSvgIcon(`Cross.svg`);
    const [onlyZipcodeWarning, setOnlyZipcodeWarning] = useState(false);
    const initialForceGroupOrder = forceOnGroupOrder !== null;
    const [grpOrderDeliveryType, setGrpOrderDeliveryType] =
        useState(forceOnGroupOrder);
    const hasPickupAreas = pickupAreas && pickupAreas.length > 0;
    const addressPopupRef = useRef(null);
    const { FEATURE_GROUP_ORDER } = useFeatureFlags();

    const formik = useFormik({
        initialValues: initialFormikValues,
        onSubmit: ({ selectedLocation, zip }) => {
            if (selectedLocation === null) {
                return;
            }

            onAddressSelect({
                selectedAddress: selectedLocation,
                isDelivery: zip !== DELIVERY_TYPES.PICKUP,
            });
        },
    });

    useEffect(() => {
        const { location, zip, selectedLocation } = formik.values;

        if (!formik.dirty) {
            return;
        }

        setLocationQuery({
            zip,
            location,
        });

        setOnlyZipcodeWarning(false);

        // reset selectedLocation if text changes
        if (
            location !== selectedLocation?.text &&
            zip !== DELIVERY_TYPES.PICKUP
        ) {
            formik.setFieldValue("selectedLocation", null);
        }
    }, [formik.values.zip, formik.values.location]);

    useEffect(() => {
        formik.resetForm();
    }, [deliveryType, forcePickup, grpOrderDeliveryType]);

    const optionStyle = (isSelected) =>
        `address-popup__option ${isSelected ? "active" : ""}`;

    useEffect(() => {
        window.posthog?.capture("postcode popup opened");
        if (!window.posthog?.sessionRecording.started) {
            window.posthog?.startSessionRecording?.();
            return () => {
                setTimeout(() => {
                    window.posthog?.stopSessionRecording?.();
                }, 2000);
            };
        }
    }, []);

    const renderPickupAreas = ({ borderless } = { borderless: false }) => {
        if (!hasPickupAreas) {
            return null;
        }

        return (
            <div
                className={`address-popup__options ${
                    borderless ? "address-popup__options--borderless" : ""
                }`}>
                {pickupAreas.map((area) => {
                    const isSelected =
                        area.id === formik.values.selectedLocation?.id;
                    return (
                        <button
                            onClick={() => {
                                formik.setValues({
                                    zip: DELIVERY_TYPES.PICKUP,
                                    location: "",
                                    selectedLocation: area,
                                });
                            }}
                            key={`area-${area.id}`}
                            className={optionStyle(isSelected)}>
                            <img src={shopIcon} alt={`area-${area.name}`} />
                            {area.name}
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderSearchDelivery = () => {
        const hasInputedZip = formik.values.zip.length > 0;
        const hasInputValidZip = formik.values.zip.length >= ZIP_LENGTH;
        const hasSuggestions = suggestions.length > 0 && formik.dirty;
        const showSuggestions = formik.values.selectedLocation === null;

        const pickSuggestion = (suggestion) => {
            checkPlaceDetails(suggestion.id);
            formik.setValues({
                zip: formik.values.zip,
                location: suggestion.text,
                selectedLocation: suggestion,
            });
        };

        return (
            <>
                <TextInput
                    placeholder={labels.zipPlaceholder}
                    bindName="zip"
                    value={formik.values.zip}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onlyNumbers
                    onlyNumberWarning={() =>
                        !hasInputedZip && setOnlyZipcodeWarning(true)
                    }
                    customStyle={!hasInputValidZip ? "input--rounded" : ""}
                />
                {hasInputValidZip && (
                    <TextInput
                        placeholder={labels.locPlaceholder}
                        bindName="location"
                        value={formik.values.location}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        customStyle="input--bottom"
                    />
                )}

                {!showSuggestions && noHouseNumberError && (
                    <small className="address-popup__error">
                        {labels.noHouseNumberError}
                    </small>
                )}

                {onlyZipcodeWarning && (
                    <small className="address-popup__error">
                        {labels.zipcodeWarning}
                    </small>
                )}

                {showSuggestions && hasSuggestions && (
                    <div className="address-popup__options">
                        {suggestions.map((suggestion) => {
                            const isSelected =
                                suggestion.id ===
                                formik.values.selectedLocation?.id;

                            return (
                                <button
                                    onClick={() => pickSuggestion(suggestion)}
                                    key={`address-${suggestion.id}`}
                                    className={
                                        optionStyle(isSelected) +
                                        " ph-no-capture"
                                    }>
                                    {suggestion.text}
                                </button>
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    const renderDeliveryContent = () => (
        <>
            <h6 className="address-popup__title">{labels.title}</h6>
            {tab === TABS.GROUP_ORDER && (
                <div className="address-popup__grp-order-notif">
                    <InfoIcon />
                    <p>{i18next.t("createGroupOrder.subtitle")}</p>
                </div>
            )}
            <DeliveryTypeSwitcher
                bicycleIcon={bicycleIcon}
                deliveryType={deliveryType}
                labels={labels}
                setDeliveryType={setDeliveryType}
                shopIcon={shopIcon}
            />
            {deliveryType === DELIVERY_TYPES.DELIVERY
                ? renderSearchDelivery()
                : renderPickupAreas()}
        </>
    );

    const renderForcePickup = () => {
        return (
            <>
                <ForcePickupNotification
                    dangerIcon={dangerIcon}
                    labels={labels}
                />
                {renderPickupAreas({ borderless: true })}
            </>
        );
    };

    const isSubmitDisabled =
        formik.values.selectedLocation === null || noHouseNumberError;

    if (initialForceGroupOrder) {
        const onDelivery = grpOrderDeliveryType === DELIVERY_TYPES.DELIVERY;
        const renderForceGroupOrderContent = onDelivery
            ? renderSearchDelivery
            : () => renderPickupAreas({ borderless: true });
        const buttonLabel = forcePickup
            ? labels.newAddress
            : onDelivery
            ? labels.goToPickup
            : labels.goToDelivery;

        return (
            <div className="address-popup" ref={addressPopupRef}>
                <span
                    className="address-popup__close-btn address-popup__close-btn--inside"
                    onClick={() => {
                        GoogleTagUtil.trackPostcodePopup("close", {
                            postcode: formik.values.zip,
                            street: !!formik.values.location,
                        });
                        setOpen(false);
                    }}>
                    <X />
                </span>
                <div className="address-popup__content">
                    <h6 className="address-popup__title">{labels.title}</h6>
                    {forcePickup
                        ? renderForcePickup()
                        : renderForceGroupOrderContent()}

                    <div className="address-popup__footer">
                        <Button
                            label={labels.save}
                            variant="primary"
                            size="SM"
                            disabled={isSubmitDisabled}
                            onClick={formik.submitForm}
                            loading={formik.isSubmitting}
                        />
                        <SimpleButton
                            customStyle="address-popup__text-btn"
                            onClick={() => {
                                if (forcePickup) {
                                    setForcePickup(false);
                                    setGrpOrderDeliveryType(
                                        DELIVERY_TYPES.DELIVERY
                                    );
                                    return;
                                }

                                setGrpOrderDeliveryType((prev) =>
                                    prev === DELIVERY_TYPES.PICKUP
                                        ? DELIVERY_TYPES.DELIVERY
                                        : DELIVERY_TYPES.PICKUP
                                );
                            }}
                            label={buttonLabel}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Default variant
    return (
        <div className="address-popup" ref={addressPopupRef}>
            {!forceSelection && (
                <button
                    className="address-popup__close-btn"
                    onClick={() => {
                        GoogleTagUtil.trackPostcodePopup("close", {
                            postcode: formik.values.zip,
                            street: !!formik.values.location,
                        });
                        setOpen(false);
                    }}>
                    <X />
                </button>
            )}

            {FEATURE_GROUP_ORDER && !forcePickup && (
                <OrderTypeTabs tab={tab} setTab={setTab} labels={labels} />
            )}

            <div className="address-popup__content">
                {!forcePickup ? renderDeliveryContent() : renderForcePickup()}

                <div className="address-popup__footer">
                    <Button
                        label={
                            tab === TABS.SINGLE_ORDER
                                ? labels.save
                                : labels.startGroupOrder
                        }
                        variant="primary"
                        size="SM"
                        disabled={isSubmitDisabled}
                        onClick={formik.submitForm}
                        loading={formik.isSubmitting}
                    />
                    {forcePickup && (
                        <SimpleButton
                            customStyle="address-popup__text-btn"
                            onClick={() => setForcePickup(false)}
                            label={labels.newAddress}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

AddressPopup.propTypes = AddressPopupPropTypes;
AddressPopup.defaultProps = {
    labels: AddressPopupLabels,
};
export default AddressPopup;
